import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { toast } from 'react-toastify';

import { GET_CURRENT_USER } from '../../../graphql/GET_CURRENT_USER';
import { GET_PROFESSIONS_BY_FILTER_QUERY } from '../../../graphql/GET_PROFESSIONS_BY_FILTER_QUERY';
import { GET_CURRENT_USER_me_desiredCollaborationProfession } from '../../../graphql/types/GET_CURRENT_USER';
import { UPDATE_USER_DESIRED_COLLABORATION_PROFESSION } from '../../../graphql/UPDATE_USER_DESIRED_COLLABORATION_PROFESSION';
import {
  IHandleOptionSelect,
  useAutocomplete
} from '../../../hooks/useAutocomplete';
import autoCompleteStyles from './autocompleteStyles';
import searchIcon from '../../../assets/svg/search.svg';
import './OnboardingForm/onboarding.sass';

interface IUserCollaborateWithProps {
  desiredCollaborationProfession:
    | GET_CURRENT_USER_me_desiredCollaborationProfession
    | IHandleOptionSelect
    | null;
  hideTitle?: boolean;
}

const UserCollaborateWith: React.FunctionComponent<IUserCollaborateWithProps> = ({
  desiredCollaborationProfession,
  hideTitle = false
}: IUserCollaborateWithProps) => {
  const [inputValue, setInputValue] = useState('');
  const [updateUserDesiredCollaborationProfession, { error }] = useMutation(
    UPDATE_USER_DESIRED_COLLABORATION_PROFESSION,
    {
      onCompleted: () =>
        toast.success(
          'User desired collaboration profession successfully updated.'
        ),
      refetchQueries: [{ query: GET_CURRENT_USER }],
      awaitRefetchQueries: true
    }
  );

  useEffect(() => {
    if (error) {
      toast.error(error.message.replace('GraphQL error: ', ''));
    }
  }, [error]);

  const collaborateWithOptsSelectCb = async (params: IHandleOptionSelect) => {
    if (!params) {
      return;
    }

    const { __isNew__, label, value } = params;

    await updateUserDesiredCollaborationProfession({
      variables: { professionName: __isNew__ ? value : label }
    });
  };

  const handleRemove = async () => {
    await updateUserDesiredCollaborationProfession({
      variables: { professionName: '' }
    });
  };

  const { loadOptions, loading } = useAutocomplete({
    query: GET_PROFESSIONS_BY_FILTER_QUERY
  });

  const hasSelectedProfession =
    desiredCollaborationProfession &&
    typeof desiredCollaborationProfession === 'object' &&
    desiredCollaborationProfession.label &&
    desiredCollaborationProfession.label.trim() !== '';

  return (
    <form className="m-b-1">
      <div className="profession-selection-container">
        <div className="profession-search-wrapper">
          <img
            src={searchIcon}
            alt=""
            className={`search-icon ${inputValue ? 'is-hidden' : ''}`}
          />

          <div className="profession-input-container">
            <AsyncCreatableSelect
              cacheOptions={true}
              isClearable={true}
              className="profession-async-select"
              classNamePrefix="react-select"
              escapeClearsValue={true}
              backspaceRemovesValue={true}
              isLoading={loading}
              onChange={collaborateWithOptsSelectCb}
              onInputChange={(value) => setInputValue(value)}
              loadOptions={loadOptions}
              placeholder="Search to add"
              styles={autoCompleteStyles}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null
              }}
              value={null}
            />
            {hasSelectedProfession && (
              <div className="selected-professions-container">
                <div className="profession-tag">
                  <span className="profession-name">
                    {desiredCollaborationProfession.label}
                  </span>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={handleRemove}
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default UserCollaborateWith;
